// Clima del Dashboard IA: íconos 3D y "cielo" animado de fondo para cada día u hora del gráfico.
//
// Íconos: Microsoft Fluent Emoji 3D (MIT, © Microsoft Corporation) — https://github.com/microsoft/fluentui-emoji
// Copiados acá (achicados a 96 px) para no depender de un CDN. Licencia en LICENSE-fluentui-emoji.txt.
//
// El cielo se dibuja en SVG detrás de las barras: degradé según el tiempo, brillo de sol, nube tenue,
// lluvia que cae (más densa cuanto más mm), relámpagos en tormenta, niebla y nieve.
// Las animaciones son solo transform/opacity sobre un elemento por franja (patrones SVG), con tope de franjas animadas,
// y se apagan con prefers-reduced-motion.
import React from 'react';
import sun from './sun.png';
import sunCloud from './sun_behind_cloud.png';
import cloud from './cloud.png';
import rain from './cloud_with_rain.png';
import sunRain from './sun_behind_rain_cloud.png';
import storm from './cloud_with_lightning_and_rain.png';
import fog from './fog.png';
import snow from './snowflake.png';
import moon from './crescent_moon.png';

export const WX_ICONS = { sol: sun, parcial: sunCloud, nublado: cloud, chaparron: sunRain, lluvia: rain, tormenta: storm, niebla: fog, nieve: snow, noche: moon };

// Degradé del cielo (arriba → abajo) con su opacidad. Suave: las barras tienen que seguir leyéndose.
const SKIES = {
  sol: ['#8ecbff', 0.42, '#eaf6ff', 0.18],
  parcial: ['#a9cdea', 0.42, '#eef5fb', 0.18],
  nublado: ['#aeb8c5', 0.42, '#e7ebf0', 0.22],
  niebla: ['#dfe4ea', 0.7, '#f4f6f8', 0.45],
  chaparron: ['#97a9bc', 0.45, '#dfe6ee', 0.22],
  lluvia: ['#6f8399', 0.5, '#cfd8e2', 0.26],
  tormenta: ['#3e4859', 0.55, '#a3acb9', 0.3],
  nieve: ['#c4d3e3', 0.5, '#f2f6fa', 0.3],
  noche: ['#1d2840', 0.5, '#3a4864', 0.3],
};

// Colores para las pastillas de la leyenda (mismo cielo, en CSS)
export const skyCss = (kind) => {
  const s = SKIES[kind] || SKIES.nublado;
  const a = (hex, o) => hex + Math.round(o * 255).toString(16).padStart(2, '0');
  return `linear-gradient(180deg, ${a(s[0], Math.min(1, s[1] * 0.9))}, ${a(s[2], Math.min(1, s[3] + 0.3))})`;
};

export const SKY_CSS = `
@keyframes iadRain { from { transform: translateY(-40px) } to { transform: translateY(0) } }
@keyframes iadSnow { from { transform: translateY(-36px) } to { transform: translateY(0) } }
@keyframes iadFog { from { transform: translateX(-14px) } to { transform: translateX(14px) } }
@keyframes iadFlash { 0%, 88%, 100% { opacity: 0 } 89% { opacity: .55 } 90% { opacity: .08 } 91.5% { opacity: .4 } 93% { opacity: 0 } }
.iad-sky-rain { animation: iadRain var(--d, .8s) linear infinite }
.iad-sky-snow { animation: iadSnow 5s linear infinite }
.iad-sky-fog { animation: iadFog 7s ease-in-out infinite alternate }
.iad-sky-flash { animation: iadFlash 9s linear infinite; opacity: 0 }
.iad-static .iad-sky-rain, .iad-static .iad-sky-snow, .iad-static .iad-sky-fog { animation: none }
.iad-static .iad-sky-flash { animation: none; opacity: 0 }
@media (prefers-reduced-motion: reduce) {
  .iad-sky-rain, .iad-sky-snow, .iad-sky-fog { animation: none }
  .iad-sky-flash { animation: none; opacity: 0 }
}`;

// Gotas por baldosa de 24×40 según intensidad (1 llovizna … 3 fuerte)
const DROPS = {
  1: [[5, 6], [17, 24]],
  2: [[3, 4], [11, 18], [19, 8], [8, 30]],
  3: [[2, 2], [8, 14], [14, 4], [20, 20], [5, 26], [12, 33], [18, 34], [23, 10]],
};

// Definiciones compartidas (degradés y patrones). uid evita choques si hay más de un gráfico en la página.
export function SkyDefs({ uid }) {
  return (
    <>
      {Object.entries(SKIES).map(([k, s]) => (
        <linearGradient key={k} id={`${uid}-g-${k}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={s[0]} stopOpacity={s[1]} />
          <stop offset="100%" stopColor={s[2]} stopOpacity={s[3]} />
        </linearGradient>
      ))}
      <radialGradient id={`${uid}-sun`} cx="50%" cy="0%" r="75%">
        <stop offset="0%" stopColor="#ffd36e" stopOpacity={0.55} />
        <stop offset="45%" stopColor="#ffe7a8" stopOpacity={0.18} />
        <stop offset="100%" stopColor="#fff4d6" stopOpacity={0} />
      </radialGradient>
      <linearGradient id={`${uid}-haze`} x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%" stopColor="#fff" stopOpacity={0} />
        <stop offset="50%" stopColor="#fff" stopOpacity={0.75} />
        <stop offset="100%" stopColor="#fff" stopOpacity={0} />
      </linearGradient>
      {[1, 2, 3].map((lv) => (
        <pattern key={lv} id={`${uid}-rain${lv}`} width="24" height="40" patternUnits="userSpaceOnUse">
          {DROPS[lv].map(([x, y]) => (
            <line key={`${x}-${y}`} x1={x} y1={y} x2={x - 1.5} y2={y + (lv === 3 ? 11 : 8)} stroke={lv === 3 ? '#46617d' : '#5b7894'} strokeOpacity={0.35 + lv * 0.08} strokeWidth={lv === 1 ? 0.8 : 1} strokeLinecap="round" />
          ))}
        </pattern>
      ))}
      <pattern id={`${uid}-snow`} width="30" height="36" patternUnits="userSpaceOnUse">
        <circle cx="6" cy="6" r="1.6" fill="#fff" fillOpacity={0.95} />
        <circle cx="20" cy="17" r="1.2" fill="#fff" fillOpacity={0.9} />
        <circle cx="11" cy="28" r="1.8" fill="#fff" fillOpacity={0.95} />
      </pattern>
    </>
  );
}

// Una franja de cielo. Se usa como `shape` de un ReferenceArea (recibe x, y, width, height).
export function SkyBand({ x, y, width, height, uid, band, animate }) {
  if (!(width > 0) || !(height > 0)) return null;
  const { kind, level, night } = band;
  const clip = `${uid}-c-${band.a}`;
  const rainy = kind === 'chaparron' || kind === 'lluvia' || kind === 'tormenta';
  const lv = kind === 'tormenta' ? 3 : kind === 'lluvia' ? Math.max(2, level || 2) : Math.min(2, level || 1);
  const cls = animate ? '' : 'iad-static';
  return (
    <g className={cls} pointerEvents="none">
      <clipPath id={clip}><rect x={x} y={y} width={width} height={height} /></clipPath>
      <g clipPath={`url(#${clip})`}>
        <rect x={x} y={y} width={width} height={height} fill={`url(#${uid}-g-${kind})`} />
        {(kind === 'sol' || kind === 'parcial') && <rect x={x} y={y} width={width} height={height * 0.7} fill={`url(#${uid}-sun)`} opacity={kind === 'sol' ? 1 : 0.55} />}
        {night && kind !== 'noche' && <rect x={x} y={y} width={width} height={height} fill="#1d2840" fillOpacity={0.18} />}
        {kind === 'parcial' && <Puff cx={x + width / 2} cy={y + 18} w={Math.min(width * 0.8, 60)} op={0.75} />}
        {kind === 'nublado' && <>
          <Puff cx={x + width * 0.35} cy={y + 14} w={Math.min(width * 0.7, 70)} op={0.55} />
          <Puff cx={x + width * 0.72} cy={y + 26} w={Math.min(width * 0.6, 56)} op={0.45} />
        </>}
        {rainy && (
          <g className="iad-sky-rain" style={{ '--d': `${lv === 3 ? 0.55 : lv === 2 ? 0.75 : 1}s` }}>
            <rect x={x} y={y - 40} width={width} height={height + 40} fill={`url(#${uid}-rain${lv})`} />
          </g>
        )}
        {kind === 'tormenta' && (
          <rect className="iad-sky-flash" x={x} y={y} width={width} height={height} fill="#f5f7ff" style={{ animationDelay: `${(band.a * 1.7) % 9}s` }} />
        )}
        {kind === 'nieve' && (
          <g className="iad-sky-snow"><rect x={x} y={y - 36} width={width} height={height + 36} fill={`url(#${uid}-snow)`} /></g>
        )}
        {kind === 'niebla' && (
          <g className="iad-sky-fog">
            {[0.3, 0.55, 0.8].map((f) => <rect key={f} x={x - 20} y={y + height * f - 10} width={width + 40} height={20} rx={10} fill={`url(#${uid}-haze)`} />)}
          </g>
        )}
      </g>
    </g>
  );
}

// Nube suave hecha con tres círculos (sin filtros, es barato)
function Puff({ cx, cy, w, op }) {
  const r = w / 4;
  return (
    <g fill="#fff" fillOpacity={op}>
      <ellipse cx={cx} cy={cy + r * 0.3} rx={w / 2} ry={r * 0.7} />
      <circle cx={cx - r * 0.7} cy={cy} r={r * 0.75} />
      <circle cx={cx + r * 0.4} cy={cy - r * 0.35} r={r} />
    </g>
  );
}

// Ícono 3D arriba de la columna (label de un ReferenceArea: recibe su viewBox)
export function WxIcon({ viewBox, wx, size }) {
  if (!viewBox || !wx) return null;
  const icon = WX_ICONS[wx.kind];
  return (
    <g>
      <title>{`${wx.label} · ${wx.detail}`}</title>
      <image href={icon} x={viewBox.x + viewBox.width / 2 - size / 2} y={viewBox.y - size - 4} width={size} height={size} style={{ filter: 'drop-shadow(0 1px 1.5px rgba(15,23,42,.18))' }} />
    </g>
  );
}
